import { useQuery } from "@tanstack/react-query";

import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import type { TenaxTask } from "@/tenax/tasks/taskGroups";

// One row of the today_items view (task 1.4, D-032)
export interface TodayItem {
  for_sales_id: number;
  relation: "owner" | "co_assignee" | "follower";
  bucket: "overdue" | "today" | "week";
  task_id: number;
  title: string;
  status: string;
  priority: number;
  due_date: string;
  owner_sales_id: number | null;
  area_id: number | null;
  origin: string;
  steps_total: number;
  steps_done: number;
  // who the task is about and the pipeline of its deal (D-045)
  related_name: string | null;
  related_pipeline: string | null;
}

// "Mario Rossi · Maestri", the name alone, the pipeline alone or nothing
export const aboutOf = (
  item: Pick<TodayItem, "related_name" | "related_pipeline">,
) =>
  [item.related_name?.trim(), item.related_pipeline?.trim()]
    .filter(Boolean)
    .join(" · ") || null;

export const toTask = (item: TodayItem): TenaxTask => ({
  id: item.task_id,
  text: item.title,
  status: item.status,
  due_date: item.due_date,
  sales_id: item.owner_sales_id,
  area_id: item.area_id,
  priority: item.priority,
  about: aboutOf(item),
});

// The view has no id column, which react-admin needs: read it directly.
export const useTodayItems = (salesId: number | undefined) =>
  useQuery({
    queryKey: ["today_items", salesId],
    enabled: salesId != null,
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("today_items")
        .select("*")
        .eq("for_sales_id", salesId!)
        .order("due_date", { ascending: true })
        .order("priority", { ascending: true });
      if (error) throw new Error(error.message);
      return data as TodayItem[];
    },
  });

// Sections of the Today screen: the person's own tasks by bucket, automatic
// follow-ups apart, then the tasks the person follows. A task appears once.
export const todaySections = (items: TodayItem[]) => {
  const own = items.filter((item) => item.relation !== "follower");
  const ownIds = new Set(own.map((item) => item.task_id));
  const manual = own.filter((item) => item.origin !== "automation");
  return {
    overdue: manual.filter((item) => item.bucket === "overdue"),
    today: manual.filter((item) => item.bucket === "today"),
    week: manual.filter((item) => item.bucket === "week"),
    automatic: own.filter((item) => item.origin === "automation"),
    following: items.filter(
      (item) => item.relation === "follower" && !ownIds.has(item.task_id),
    ),
  };
};
